import { Text } from '#app/components/Primitives/Text.js';
import { type Theme } from '#app/theme/index.js';
import { type ArrowPosition, type TooltipCompoundProps } from './types.js';
import { css, styled } from 'styled-components';

const TOOLTIP_DEFAULT_MAX_WIDTH = 494;

export const TooltipContainer = styled.div<{
  $containerWidth?: number | string;
}>`
  ${({ $containerWidth }) => css`
    display: inline-block;
    ${$containerWidth ? `width: ${$containerWidth};` : ''}
  `}
`;

export const TooltipReferenceContainer = styled.div<{
  $hideOutlineOnFocus?: boolean;
}>`
  ${({ $hideOutlineOnFocus, theme }) => css`
    cursor: pointer;
    position: relative;
    &:focus-visible {
      outline: 1px solid
        ${$hideOutlineOnFocus ? 'transparent' : theme.colorsV2.gray60};
    }
  `}
`;

export const TooltipParagraph = styled(Text).attrs({
  textStyle: 'bodySmall',
})`
  color: inherit;
  margin-bottom: 16px;
`;

export const TooltipSubtitle = styled(Text).attrs({
  as: 'span',
  textStyle: 'subtitleSmall',
})`
  color: inherit;
  margin-right: 4px;
`;

const getVariantStyles = ({
  theme,
  variant,
}: {
  theme: Theme;
  variant: TooltipCompoundProps['variant'];
}) => {
  switch (variant) {
    case 'black':
      return css`
        background-color: ${theme.colorsV2.black};
        color: ${theme.colorsV2.white};
        border: 1px solid ${theme.colorsV2.gray70};
      `;
    case 'white':
    default:
      return css`
        background-color: ${theme.colorsV2.white};
        color: ${theme.colorsV2.gray70};
      `;
  }
};

export const TooltipContent = styled(Text)<{
  $marginBottom?: number;
  maxWidth?: number | string;
  variant: TooltipCompoundProps['variant'];
  whitespace?: 'normal' | 'nowrap' | 'pre-line' | 'pre-wrap';
}>`
  ${({ $marginBottom, maxWidth, theme, variant, whitespace }) => css`
    ${getVariantStyles({ theme, variant })};
    box-shadow: ${variant === 'white' ? theme.boxShadow.light : 'none'};
    max-width: ${typeof maxWidth === 'string'
      ? maxWidth
      : `${maxWidth ?? TOOLTIP_DEFAULT_MAX_WIDTH}px`};
    border-radius: 10px;
    padding: 12px 16px;
    overflow-wrap: break-word;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.42;
    letter-spacing: 0.15px;
    white-space: ${whitespace ?? 'pre-line'};
    margin-bottom: ${$marginBottom ?? 0}px;
  `}
`;

const getArrowPosition = (position: ArrowPosition) => {
  switch (position) {
    case 'bottom-left':
      return css`
        bottom: -16px;
        left: 32px;
      `;
    case 'bottom-right':
      return css`
        bottom: -16px;
        right: 16px;
      `;
    case 'left-bottom':
      return css`
        bottom: 16px;
        left: 1px;
      `;
    case 'left-center':
      return css`
        top: 47.5%;
        left: 1px;
      `;
    case 'left-top':
      return css`
        top: 32px;
        left: 1px;
      `;
    case 'right-bottom':
      return css`
        bottom: 16px;
        right: -16px;
      `;
    case 'right-center':
      return css`
        top: 47.5%;
        right: -16px;
      `;
    case 'right-top':
      return css`
        top: 32px;
        right: -16px;
      `;
    case 'top-center':
      return css`
        right: 40%;
        top: 1px;
      `;
    case 'top-left':
      return css`
        left: 32px;
        top: 1px;
      `;
    case 'top-right':
      return css`
        right: 16px;
        top: 1px;
      `;
    case 'bottom-center':
    default:
      return css`
        bottom: -16px;
        right: 40%;
      `;
  }
};

export const Arrow = styled.div<{
  position: ArrowPosition;
  variant: TooltipCompoundProps['variant'];
}>`
  ${({ position, theme, variant }) => css`
    &::before {
      ${getArrowPosition(position)}
      ${getVariantStyles({ theme, variant })}
      transform: rotate(45deg) translateX(-50%);
      position: absolute;
      z-index: -1;
      display: block;
      content: '';
      width: 24px;
      height: 24px;
      border-radius: 2px;
      z-index: ${theme.zIndices.tooltip + 1};
    }
  `}
`;
